import { useState } from 'react';
import { ApartmentOutlined, InfoCircleOutlined, OrderedListOutlined, SearchOutlined } from '@ant-design/icons';

import { MenuThaoTac } from '../../../../components/shared';
import { Empty, Input, Select } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { soVN } from '../../../../lib/dinh-dang';
import { LOAI_LUONG } from '../../constants';
import { KHONG_GAN, LOAI_WORKFLOW, loaiCua, locWorkflow } from '../../nghiep-vu/workflow';
import type { WorkflowCoProject } from '../../nghiep-vu/workflow';
import type { ProjectOpenRpa } from '../../types';
import { IconLoaiLuong } from '../chung';

/** Cột trái của Danh sách luồng: tìm, lọc theo Project, Workflow nhóm theo loại luồng. */
export function DanhSachWorkflow({
  items,
  projects,
  dangChon,
  onChon,
  onXemThongTin,
}: {
  items: WorkflowCoProject[];
  projects: ProjectOpenRpa[];
  dangChon: string;
  onChon: (id: string) => void;
  onXemThongTin: (id: string) => void;
}) {
  const { t, locale } = useT();
  const [tim, setTim] = useState('');
  const [project, setProject] = useState('');
  const dong = locWorkflow(items, { tim, project });

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-3">
      <Input
        allowClear
        prefix={<SearchOutlined aria-hidden />}
        placeholder={t('rpaWorkflow.tim')}
        aria-label={t('rpaWorkflow.tim')}
        value={tim}
        onChange={(e) => setTim(e.target.value)}
      />
      <Select<string>
        value={project}
        onChange={setProject}
        aria-label={t('rpaWorkflow.locProject')}
        showSearch={{ optionFilterProp: 'label' }}
        className="w-full"
        options={[
          { value: '', label: t('rpaWorkflow.tatCaProject') },
          { value: KHONG_GAN, label: t('rpaWorkflow.khongThuocProject') },
          ...projects.map((p) => ({ value: p.id, label: p.name })),
        ]}
      />
      {items.length === 0 ? (
        <Empty icon={ApartmentOutlined} title={t('rpaWorkflow.rong')} description={t('rpaWorkflow.rongMoTa')} className="py-6" />
      ) : dong.length === 0 ? (
        <Empty icon={ApartmentOutlined} title={t('rpaWorkflow.rongLoc')} className="py-6" />
      ) : (
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain pr-1">
          {LOAI_WORKFLOW.map((loai) => {
            const nhom = dong.filter((w) => loaiCua(w) === loai);
            if (nhom.length === 0) return null;
            const l = LOAI_LUONG[loai];
            return (
              <section key={loai} aria-label={t(l.tieuDe)}>
                <h3 className={cn('mb-2 flex items-center justify-between gap-2 border-l-2 pl-2 text-2xs font-bold uppercase', l.vien, l.chu)}>
                  <span className="min-w-0">{t(l.tieuDe)}</span>
                  <span className="shrink-0 rounded-full bg-subtle px-2 py-0.5 tabular-nums text-content-secondary">
                    {soVN(nhom.length, locale)}
                  </span>
                </h3>
                <ul className="space-y-1">
                  {nhom.map((w) => {
                    const chon = w.id === dangChon;
                    return (
                      <li
                        key={w.id}
                        className={cn(
                          'flex min-w-0 items-center rounded-lg border transition-colors',
                          chon ? cn(l.vien, l.nen) : 'border-transparent hover:bg-subtle',
                        )}
                      >
                        <button
                          type="button"
                          aria-current={chon ? 'true' : undefined}
                          onClick={() => onChon(w.id)}
                          className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-2 py-2.5 text-left"
                        >
                          <span className={cn('flex size-9 shrink-0 items-center justify-center rounded', l.nen, l.chu)}>
                            <IconLoaiLuong loai={loai} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block break-words text-sm font-medium leading-5 text-content-primary">{w.name || w.id}</span>
                            <span className="block truncate text-xs text-content-muted">
                              {t('rpaWorkflow.thuocProject', { ten: w.projectName || t('rpaWorkflow.khongThuocProject') })}
                            </span>
                          </span>
                        </button>
                        <span className="mr-1 shrink-0">
                          <MenuThaoTac
                            tenBanGhi={w.name || w.id}
                            muc={[
                              { khoa: 'buoc', nhan: t('rpaWorkflow.xemBuoc'), icon: <OrderedListOutlined />, nhom: 'xem', onChon: () => onChon(w.id) },
                              { khoa: 'thong-tin', nhan: t('rpaWorkflow.xemThongTin'), icon: <InfoCircleOutlined />, nhom: 'xem',
                                onChon: () => onXemThongTin(w.id) },
                            ]}
                          />
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
